import { useCallback, useEffect, useMemo, useState } from "react";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";

import ConfirmDialog from "components/ConfirmDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController, setMessage } from "context";
import type { IAtributo, IAtributoOpcao, TipoAtributo } from "interfaces/loja";
import { normalizeString } from "helpers/normalizeString";
import { LojaDataGrid, LojaPageContainer } from "../common";
import {
  addOpcaoToAtributo,
  createAtributo,
  deleteAtributo,
  deleteOpcaoAtributo,
  getAllAtributos,
  updateAtributo,
  updateOpcaoAtributo,
} from "services/lojaAtributos";
import {
  ATRIBUTOS_ACCENT,
  atributosToolbarSx,
  emptyStateSx,
  fieldSx,
  gridSx,
  saveButtonSx,
  tipoToggleSx,
  actionIconSx,
} from "./atributos.theme";
import AtributoFormDialog from "./components/AtributoFormDialog";
import AtributoOpcoesDialog from "./components/AtributoOpcoesDialog";
import OpcaoFormDialog from "./components/OpcaoFormDialog";
import {
  permiteOpcoes,
  TIPO_FILTER_OPTIONS,
  TIPO_LABELS,
  tipoChipSx,
} from "./components/atributos.helpers";

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? ATRIBUTOS_ACCENT : "text.primary"}
        sx={{ fontSize: { xs: "1.25rem", sm: "2rem" } }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function LojaAtributos() {
  const [, dispatch] = useMaterialUIController();
  const [rows, setRows] = useState<IAtributo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState<TipoAtributo | "">("");

  const [openAttr, setOpenAttr] = useState(false);
  const [editing, setEditing] = useState<IAtributo | null>(null);
  const [savingAttr, setSavingAttr] = useState(false);

  const [opcoesAttr, setOpcoesAttr] = useState<IAtributo | null>(null);
  const [openOpcao, setOpenOpcao] = useState(false);
  const [editingOpcao, setEditingOpcao] = useState<IAtributoOpcao | null>(null);
  const [savingOpcao, setSavingOpcao] = useState(false);

  const [openDelete, setOpenDelete] = useState<{
    open: boolean;
    id?: number;
    opcao?: boolean;
    opcaoId?: number;
    attrId?: number;
    label?: string;
  }>({ open: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, status } = await getAllAtributos();
      if (status === 200) setRows(data.data || []);
    } catch (error: unknown) {
      const msg =
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "message" in error.response.data &&
        typeof error.response.data.message === "string"
          ? error.response.data.message
          : "Falha ao carregar atributos.";
      setMessage(dispatch, { open: true, severity: "error", text: msg });
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!opcoesAttr) return;
    const atualizado = rows.find((r) => r.id === opcoesAttr.id);
    if (atualizado) setOpcoesAttr(atualizado);
  }, [rows, opcoesAttr?.id]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (tipoFilter && r.tipo !== tipoFilter) return false;
      if (!term) return true;
      return (
        r.nome.toLowerCase().includes(term) ||
        r.slug.toLowerCase().includes(term) ||
        TIPO_LABELS[r.tipo].toLowerCase().includes(term)
      );
    });
  }, [rows, search, tipoFilter]);

  const stats = useMemo(() => {
    const comOpcoes = rows.filter((r) => permiteOpcoes(r.tipo)).length;
    const totalOpcoes = rows.reduce((acc, r) => acc + (r.opcoes?.length ?? 0), 0);
    return { total: rows.length, comOpcoes, totalOpcoes };
  }, [rows]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setOpenAttr(true);
  }, []);

  const openEdit = useCallback((atr: IAtributo) => {
    setEditing(atr);
    setOpenAttr(true);
  }, []);

  const openGerenciarOpcoes = useCallback((atr: IAtributo) => {
    setOpcoesAttr(atr);
  }, []);

  const openCreateOpcao = useCallback(() => {
    setEditingOpcao(null);
    setOpenOpcao(true);
  }, []);

  const openEditOpcao = useCallback((op: IAtributoOpcao) => {
    setEditingOpcao(op);
    setOpenOpcao(true);
  }, []);

  const onSaveAttr = useCallback(
    async (form: { nome: string; tipo: TipoAtributo }) => {
      setSavingAttr(true);
      try {
        const payload = {
          nome: form.nome,
          slug: normalizeString(form.nome),
          tipo: form.tipo,
        };
        if (editing) {
          await updateAtributo(String(editing.id), payload);
          setMessage(dispatch, {
            open: true,
            severity: "success",
            text: "Atributo atualizado.",
          });
        } else {
          await createAtributo(payload);
          setMessage(dispatch, {
            open: true,
            severity: "success",
            text: "Atributo criado.",
          });
        }
        setOpenAttr(false);
        await load();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Falha ao salvar.";
        setMessage(dispatch, { open: true, severity: "error", text: msg });
      } finally {
        setSavingAttr(false);
      }
    },
    [editing, load, dispatch],
  );

  const onSaveOpcao = useCallback(
    async (form: { label: string; valor: string }) => {
      if (!opcoesAttr) return;
      setSavingOpcao(true);
      try {
        const payload = {
          label: form.label,
          valor: form.valor,
        };
        if (editingOpcao) {
          await updateOpcaoAtributo(
            String(opcoesAttr.id),
            String(editingOpcao.id),
            payload,
          );
          setMessage(dispatch, {
            open: true,
            severity: "success",
            text: "Opção atualizada.",
          });
        } else {
          await addOpcaoToAtributo(String(opcoesAttr.id), payload);
          setMessage(dispatch, {
            open: true,
            severity: "success",
            text: "Opção adicionada.",
          });
        }
        setOpenOpcao(false);
        await load();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Falha ao salvar opção.";
        setMessage(dispatch, { open: true, severity: "error", text: msg });
      } finally {
        setSavingOpcao(false);
      }
    },
    [opcoesAttr, editingOpcao, load, dispatch],
  );

  const onConfirmDelete = useCallback(async () => {
    if (!openDelete.id) return;
    try {
      if (openDelete.opcao && openDelete.attrId && openDelete.opcaoId) {
        await deleteOpcaoAtributo(
          String(openDelete.attrId),
          String(openDelete.opcaoId),
        );
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: "Opção excluída.",
        });
      } else {
        await deleteAtributo(String(openDelete.id));
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: "Atributo excluído.",
        });
        if (opcoesAttr?.id === openDelete.id) setOpcoesAttr(null);
      }
      setOpenDelete({ open: false });
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { status?: number }; message?: string };
      const msg =
        err?.response?.status === 409
          ? "Não é possível excluir: atributo ou opção em uso em variações."
          : err?.message || "Falha ao excluir.";
      setMessage(dispatch, { open: true, severity: "error", text: msg });
    }
  }, [openDelete, load, dispatch, opcoesAttr?.id]);

  const columns: GridColDef<IAtributo>[] = useMemo(
    () => [
      {
        field: "nome",
        headerName: "Atributo",
        flex: 1,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<IAtributo>) => (
          <Box minWidth={0}>
            <MDTypography variant="button" fontWeight="medium" noWrap>
              {params.row.nome}
            </MDTypography>
            <MDTypography variant="caption" color="text.secondary" display="block" noWrap>
              {params.row.slug}
            </MDTypography>
          </Box>
        ),
      },
      {
        field: "tipo",
        headerName: "Tipo",
        width: 140,
        renderCell: (params: GridRenderCellParams<IAtributo>) => {
          const chip = tipoChipSx(params.row.tipo);
          return (
            <Chip
              size="small"
              label={TIPO_LABELS[params.row.tipo]}
              sx={{ bgcolor: chip.bg, color: chip.fg, fontWeight: 600 }}
            />
          );
        },
      },
      {
        field: "opcoes",
        headerName: "Opções",
        width: 100,
        sortable: false,
        align: "center",
        headerAlign: "center",
        valueGetter: (params) => params.row.opcoes?.length ?? 0,
        renderCell: (params: GridRenderCellParams<IAtributo>) => {
          if (!permiteOpcoes(params.row.tipo)) {
            return (
              <MDTypography variant="caption" color="text.secondary">
                —
              </MDTypography>
            );
          }
          const count = params.row.opcoes?.length ?? 0;
          return (
            <Chip
              size="small"
              label={count}
              variant={count > 0 ? "filled" : "outlined"}
              sx={
                count > 0
                  ? { bgcolor: "rgba(249, 96, 48, 0.12)", color: ATRIBUTOS_ACCENT, fontWeight: 700 }
                  : undefined
              }
            />
          );
        },
      },
      {
        field: "acoes",
        headerName: "Ações",
        width: 140,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams<IAtributo>) => {
          const atr = params.row;
          return (
            <MDBox display="flex" justifyContent="center" gap={0.25}>
              <Tooltip
                title={
                  permiteOpcoes(atr.tipo)
                    ? "Gerenciar opções"
                    : "Este tipo não usa opções pré-cadastradas"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => openGerenciarOpcoes(atr)}
                    sx={permiteOpcoes(atr.tipo) ? actionIconSx : { color: "grey.400" }}
                  >
                    <TuneOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Editar atributo">
                <IconButton size="small" onClick={() => openEdit(atr)} sx={actionIconSx}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Excluir atributo">
                <IconButton
                  size="small"
                  onClick={() =>
                    setOpenDelete({
                      open: true,
                      id: atr.id,
                      label: atr.nome,
                    })
                  }
                  sx={{ color: "error.main" }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MDBox>
          );
        },
      },
    ],
    [openEdit, openGerenciarOpcoes],
  );

  const isFiltered = Boolean(search.trim() || tipoFilter);
  const deleteMessage = openDelete.opcao
    ? `Excluir a opção "${openDelete.label ?? ""}" permanentemente?`
    : `Excluir o atributo "${openDelete.label ?? ""}" e todas as opções vinculadas?`;

  return (
    <LojaPageContainer
      title="Atributos"
      subtitle="Defina tipos e opções usados nas variações de produtos (tamanho, cor, etc.)."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={openCreate}
          sx={saveButtonSx}
        >
          Novo atributo
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini
            label={isFiltered ? "No filtro" : "Total cadastrados"}
            value={isFiltered ? filteredRows.length : stats.total}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Exibindo agora" value={filteredRows.length} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Com opções (Lista/Cor)" value={stats.comOpcoes} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Opções cadastradas" value={stats.totalOpcoes} />
        </Grid>
      </Grid>

      <MDBox sx={atributosToolbarSx}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CategoryOutlinedIcon sx={{ color: ATRIBUTOS_ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              {filteredRows.length} atributo{filteredRows.length === 1 ? "" : "s"}
              {isFiltered ? " no filtro" : " cadastrado" + (filteredRows.length === 1 ? "" : "s")}
            </MDTypography>
          </Stack>

          <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Tipo
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={tipoFilter}
              onChange={(_, next: TipoAtributo | "" | null) => {
                if (next === null) return;
                setTipoFilter(next);
              }}
              sx={tipoToggleSx}
            >
              {TIPO_FILTER_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value || "all-tipos"} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>

          <FormControl variant="outlined" size="small" fullWidth sx={fieldSx}>
            <InputLabel shrink htmlFor="atributos-search">
              Buscar
            </InputLabel>
            <OutlinedInput
              id="atributos-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              label="Buscar"
              notched
              placeholder="Nome, slug ou tipo…"
              endAdornment={
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
            />
          </FormControl>
        </Stack>
      </MDBox>

      {!loading && filteredRows.length === 0 ? (
        <MDBox sx={emptyStateSx}>
          <CategoryOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            {isFiltered ? "Nenhum atributo no filtro" : "Nenhum atributo cadastrado"}
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary" mb={2}>
            {isFiltered
              ? "Ajuste a busca ou o tipo selecionado."
              : "Crie atributos como Tamanho ou Cor para montar variações nos produtos."}
          </MDTypography>
          {!isFiltered ? (
            <MDButton
              variant="gradient"
              color="dark"
              startIcon={<AddOutlinedIcon />}
              onClick={openCreate}
              sx={saveButtonSx}
            >
              Criar primeiro atributo
            </MDButton>
          ) : null}
        </MDBox>
      ) : (
        <LojaDataGrid
          rows={filteredRows}
          getRowId={(r) => r.id}
          columns={columns}
          loading={loading}
          sx={gridSx}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
        />
      )}

      <AtributoFormDialog
        open={openAttr}
        editing={editing}
        saving={savingAttr}
        onClose={() => setOpenAttr(false)}
        onSave={onSaveAttr}
      />

      <AtributoOpcoesDialog
        open={Boolean(opcoesAttr)}
        atributo={opcoesAttr}
        onClose={() => setOpcoesAttr(null)}
        onAddOpcao={openCreateOpcao}
        onEditOpcao={openEditOpcao}
        onDeleteOpcao={(op) =>
          opcoesAttr &&
          setOpenDelete({
            open: true,
            id: opcoesAttr.id,
            opcao: true,
            opcaoId: op.id,
            attrId: opcoesAttr.id,
            label: op.label,
          })
        }
      />

      {opcoesAttr ? (
        <OpcaoFormDialog
          open={openOpcao}
          tipoAtributo={opcoesAttr.tipo}
          editing={editingOpcao}
          saving={savingOpcao}
          onClose={() => setOpenOpcao(false)}
          onSave={onSaveOpcao}
        />
      ) : null}

      <ConfirmDialog
        open={openDelete.open}
        title="Confirmar exclusão"
        message={deleteMessage}
        onClose={() => setOpenDelete({ open: false })}
        onConfirm={onConfirmDelete}
      />
    </LojaPageContainer>
  );
}
